import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Typography,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as ActiveIcon,
  Build as MaintenanceIcon,
  Cancel as InactiveIcon,
  DirectionsCar as CarIcon,
  VpnKey as LicenseIcon
} from '@mui/icons-material';
import { getLicenseInfo } from '../../config/vehicleLimitConfig';
import { styled } from '@mui/material/styles';

import {
  fetchVehicles,
  deleteVehicle
  // OLD LISTENERS DEAKTIVIERT - AppDataManager übernimmt Datenladung
  // startVehiclesStatusListener,
  // stopVehiclesStatusListener
} from '../../redux/slices/vehiclesSlice';
import { fetchSettings } from '../../redux/slices/settingsSlice';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  let backgroundColor;
  let textColor = 'white';
  let icon;
  
  switch (status) {
    case 'active':
      backgroundColor = theme.palette.success.main;
      icon = <ActiveIcon />;
      break;
    case 'maintenance':
      backgroundColor = theme.palette.warning.main;
      icon = <MaintenanceIcon />;
      break;
    case 'inactive':
      backgroundColor = theme.palette.error.main;
      icon = <InactiveIcon />;
      break;
    case 'available':
      backgroundColor = theme.palette.success.main;
      icon = <ActiveIcon />;
      break;
    case 'in_use':
      backgroundColor = theme.palette.primary.main;
      icon = <CarIcon />;
      break;
    default:
      backgroundColor = undefined; // Verwende Standard-Hintergrund
      textColor = 'inherit';
      icon = <InactiveIcon />;
      break;
  }
  
  return {
    color: textColor,
    backgroundColor,
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
    },
  };
});

const VehiclesList = () => {
  const dispatch = useDispatch();
  // Verwende neues AppData System statt altes vehicles slice (memoized)
  const vehicles = useSelector(state => state.appData.vehicles);
  const drivers = useSelector(state => state.appData.drivers); // Für Fahrer-Zuordnung
  const isLoading = useSelector(state => state.appData.isLoading.vehicles);
  const error = useSelector(state => state.appData.error.vehicles);

  // Lizenzlimit aus den globalen Einstellungen (Firestore)
  const vehicleLimitFromDB = useSelector(state => state.settings?.global?.vehicleLimit);
  const settingsInitialized = useSelector(state => state.settings?.isInitialized);
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('model');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuVehicle, setMenuVehicle] = useState(null);
  
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    // Settings laden falls noch nicht initialisiert (für vehicleLimit)
    if (!settingsInitialized) {
      dispatch(fetchSettings());
    }

    const loadVehicles = async () => {
      console.log("VehiclesList: Lade Fahrzeugdaten...");

      try {
        await dispatch(fetchVehicles());

        console.log("VehiclesList: Fahrzeugdaten erfolgreich geladen!");
        setDataLoaded(true);

        // AppDataManager übernimmt automatisch Echtzeit-Listener - keine manuellen Calls mehr nötig
        console.log("VehiclesList: Daten geladen, AppDataManager übernimmt Echtzeit-Updates");
      } catch (error) {
        console.error("VehiclesList: Fehler beim Laden der Fahrzeugdaten:", error);
        setDataLoaded(false);
      }
    };

    if (!dataLoaded) {
      loadVehicles();
    }
    
    // AppDataManager übernimmt Cleanup automatisch - keine manuellen Listener-Stops nötig
    return () => {
      console.log("VehiclesList: Komponente wird entfernt, AppDataManager übernimmt Cleanup");
    };
  }, [dispatch, dataLoaded]);
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };
  
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };
  
  const handleSortByChange = (event) => {
    setSortBy(event.target.value);
    setPage(0);
  };
  
  const handleOpenMenu = (event, vehicle) => {
    setMenuAnchor(event.currentTarget);
    setMenuVehicle(vehicle);
  };
  
  const handleCloseMenu = () => {
    setMenuAnchor(null);
    setMenuVehicle(null);
  };
  
  const handleDeleteClick = (vehicle) => {
    setSelectedVehicle(vehicle);
    setDeleteDialogOpen(true);
    handleCloseMenu();
  };
  
  const handleDeleteConfirm = () => {
    if (selectedVehicle) {
      dispatch(deleteVehicle(selectedVehicle.id));
      setDeleteDialogOpen(false);
      setSelectedVehicle(null);
    }
  };
  
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedVehicle(null);
  };
  
  // Filter and sort data (korrigiert für inUse Boolean statt status String)
  const filteredData = vehicles.filter(vehicle => {
    // Filter by search term
    const searchMatch = 
      vehicle.model?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vehicle.licensePlate?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by status - basiert auf inUse Boolean
    let statusMatch = true;
    if (statusFilter === 'available') {
      statusMatch = !vehicle.inUse;
    } else if (statusFilter === 'in_use') {
      statusMatch = vehicle.inUse === true;
    } else if (statusFilter === 'all') {
      statusMatch = true;
    }
    
    return searchMatch && statusMatch;
  }).sort((a, b) => {
    // Sort data
    switch (sortBy) {
      case 'model':
        return a.model.localeCompare(b.model);
      case 'license_plate':
        return a.licensePlate.localeCompare(b.licensePlate);
      case 'year':
        return (b.year || 0) - (a.year || 0);
      case 'status':
        // Sort by inUse: in_use first, then available
        return (b.inUse ? 1 : 0) - (a.inUse ? 1 : 0);
      default:
        return 0;
    }
  });
  
  // Paginate data
  const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  
  const getVehicleStatus = (vehicle) => {
    // Basiert auf inUse Boolean aus DB-Dokumentation
    if (vehicle.inUse === true) {
      return { label: 'Im Einsatz', color: 'primary' };
    } else {
      return { label: 'Verfügbar', color: 'success' };
    }
  };
  
  const translateStatus = (status) => {
    const statusMap = {
      'active': 'Verfügbar',
      'maintenance': 'Wartung',
      'inactive': 'Inaktiv',
      'available': 'Verfügbar',
      'in_use': 'Im Einsatz'
    };
    
    return statusMap[status] || status;
  };
  
  if (isLoading) {
    return <Loading message="Fahrzeuge werden geladen..." />;
  }
  
  if (error) {
    return (
      <ErrorMessage
        title="Fehler beim Laden der Fahrzeuge"
        message={error}
        onRetry={() => dispatch(fetchVehicles())}
      />
    );
  }
  
  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Fahrzeugverwaltung"
        description="Übersicht und Verwaltung aller Fahrzeuge"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Fahrzeuge', path: '/fahrzeuge' }
        ]}
        actionButton={{
          label: 'Fahrzeug hinzufügen',
          icon: <AddIcon />,
          path: '/fahrzeuge/neu'
        }}
      />
      
      {/* Lizenzinformation Banner */}
      {(() => {
        const licenseInfo = getLicenseInfo(vehicleLimitFromDB);
        const vehicleCount = vehicles.length;
        const isNearLimit = vehicleCount >= (licenseInfo.limit * 0.8);
        const isAtLimit = vehicleCount >= licenseInfo.limit;
        
        return (
          <Box sx={{ mb: 3 }}>
            <Alert 
              severity={isAtLimit ? "error" : isNearLimit ? "warning" : "info"}
              icon={<LicenseIcon />}
              sx={{ 
                mb: 2,
                '& .MuiAlert-message': { 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  width: '100%' 
                } 
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Typography variant="body1">
                  {isAtLimit ? 
                    `Fahrzeuglimit erreicht: ${vehicleCount}/${licenseInfo.limit} Fahrzeuge (${licenseInfo.tierName} Lizenz)` :
                    `Fahrzeugnutzung: ${vehicleCount}/${licenseInfo.limit} Fahrzeuge (${licenseInfo.tierName} Lizenz)`
                  }
                </Typography>
                {isAtLimit && (
                  <Button 
                    variant="outlined" 
                    size="small" 
                    color="inherit" 
                    component={Link} 
                    to="#"
                    onClick={() => alert('Bitte kontaktieren Sie den Support für ein Lizenz-Upgrade.')}
                  >
                    Lizenz erweitern
                  </Button>
                )}
              </Box>
            </Alert>
          </Box>
        );
      })()}
      
      {/* Filter und Suche */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              variant="outlined"
              label="Suche nach Modell oder Kennzeichen"
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                id="status-filter"
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="all">Alle Status</MenuItem>
                <MenuItem value="available">Verfügbar</MenuItem>
                <MenuItem value="in_use">In Benutzung</MenuItem>
                <MenuItem value="maintenance">Wartung</MenuItem>
                <MenuItem value="inactive">Inaktiv</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth>
              <InputLabel id="sort-by-label">Sortieren nach</InputLabel>
              <Select
                labelId="sort-by-label"
                id="sort-by"
                value={sortBy}
                label="Sortieren nach"
                onChange={handleSortByChange}
              >
                <MenuItem value="model">Modell</MenuItem>
                <MenuItem value="license_plate">Kennzeichen</MenuItem>
                <MenuItem value="year">Baujahr</MenuItem>
                <MenuItem value="status">Status</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <Typography variant="body2" color="textSecondary">
              {filteredData.length} Fahrzeuge gefunden
            </Typography>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Fahrzeugtabelle */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="Fahrzeugtabelle">
          <TableHead>
            <TableRow>
              <TableCell>Fahrzeug</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Kennzeichen</TableCell>
              <TableCell align="center">Kapazität</TableCell>
              <TableCell align="center">Zuletzt Genutzt</TableCell>
              <TableCell align="center">Aktueller Fahrer</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length > 0 ? (
              paginatedData.map((vehicle) => (
                <StyledTableRow key={vehicle.id}>
                  <TableCell component="th" scope="row">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CarIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                      <Box>
                        <Typography variant="subtitle2">{vehicle.model}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {vehicle.vehicleType === 1 ? 'PKW' : vehicle.vehicleType === 2 ? 'Kombi' : vehicle.vehicleType === 3 ? 'Kleinbus' : 'Unbekannt'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <StatusChip 
                      label={getVehicleStatus(vehicle).label}
                      status={vehicle.inUse ? 'in_use' : 'available'}
                      size="small"
                      icon={vehicle.inUse ? <CarIcon /> : <ActiveIcon />}
                    />
                  </TableCell>
                  <TableCell align="center">{vehicle.licensePlate}</TableCell>
                  <TableCell align="center">
                    {vehicle.vehicleType === 1 ? '4 Sitzplätze' : 
                     vehicle.vehicleType === 2 ? '6 Sitzplätze' : 
                     vehicle.vehicleType === 3 ? '8 Sitzplätze' : 
                     vehicle.capacity ? `${vehicle.capacity} Sitzplätze` : '-'}
                  </TableCell>
                  <TableCell align="center">
                    {vehicle.lastActivityTimestamp ? (
                      (() => {
                        try {
                          let date;
                          if (vehicle.lastActivityTimestamp.toDate) {
                            date = vehicle.lastActivityTimestamp.toDate();
                          } else if (vehicle.lastActivityTimestamp.seconds) {
                            date = new Date(vehicle.lastActivityTimestamp.seconds * 1000);
                          } else {
                            date = new Date(vehicle.lastActivityTimestamp);
                          }
                          return date.toLocaleDateString('de-DE') + ' ' + date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
                        } catch (error) {
                          return 'Ungültiges Datum';
                        }
                      })()
                    ) : '-'}
                  </TableCell>
                  <TableCell align="center">
                    {vehicle.driverId ? (
                      (() => {
                        const assignedDriver = drivers.find(driver => driver.id === vehicle.driverId);
                        return assignedDriver ? (
                          <Typography variant="body2">
                            {assignedDriver.name}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            Fahrer nicht gefunden
                          </Typography>
                        );
                      })()
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Kein Fahrer zugewiesen
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      aria-label="Aktionen"
                      aria-controls={`vehicle-menu-${vehicle.id}`}
                      aria-haspopup="true"
                      onClick={(e) => handleOpenMenu(e, vehicle)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body1" sx={{ py: 3 }}>
                    Keine Fahrzeuge gefunden
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Zeilen pro Seite:"
          labelDisplayedRows={
            ({ from, to, count }) => `${from}–${to} von ${count}`
          }
        />
      </TableContainer>
      
      {/* Aktions-Menü */}
      <Menu
        id={menuVehicle ? `vehicle-menu-${menuVehicle.id}` : undefined}
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem 
          component={Link} 
          to={menuVehicle ? `/fahrzeuge/${menuVehicle.id}` : '#'} 
          onClick={handleCloseMenu}
        >
          Fahrzeug anzeigen
        </MenuItem>
        <MenuItem 
          component={Link} 
          to={menuVehicle ? `/fahrzeuge/${menuVehicle.id}/bearbeiten` : '#'} 
          onClick={handleCloseMenu}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Bearbeiten
        </MenuItem>
        <MenuItem 
          onClick={() => menuVehicle && handleDeleteClick(menuVehicle)}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Löschen
        </MenuItem>
      </Menu>
      
      {/* Löschen-Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title="Fahrzeug löschen"
        content={`Möchten Sie das Fahrzeug ${selectedVehicle?.model || ''} (${selectedVehicle?.licensePlate || ''}) wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Box>
  );
};

export default VehiclesList;